import { initComponent } from './core/init-component';
import { bindAll } from './utils/bind-all';
import { initCursorFollowClose } from './utils/cursor-follow-close';

initComponent('.js-bis-drawer', (section) => {
  const product = section.dataset.product;

  // Portal the drawer to <body> so its `position: fixed` panel escapes any
  // transformed ancestor (e.g. the mini-bag upsell carousel's Swiper track,
  // whose transform would otherwise re-anchor the fixed panel and reveal it
  // over the carousel). This must also run in the theme editor (designMode):
  // duplicates are cleaned up below via the product dedupe on re-init.
  if (section.parentElement !== document.body) {
    document.body.appendChild(section);
  }
  
  const drawers = document.body.querySelectorAll<HTMLElement>(
    `.js-bis-drawer[data-product="${product}"]`
  );
  
  drawers.forEach((drawer) => {
    if (drawer !== section) {
      drawer.remove();
    }
  });

  const overlay = section.querySelector<HTMLElement>('.js-bis-drawer-overlay');
  const panel = section.querySelector<HTMLElement>('.js-bis-drawer-panel');
  const closeButton = section.querySelector<HTMLElement>('.js-bis-drawer-close');

  // Close icon that follows the cursor over the overlay (md+).
  const closeFollow = section.querySelector<HTMLElement>('.js-drawer-close-follow');
  const cursorClose = initCursorFollowClose(overlay, closeFollow);
  const contents = [
    ...section.querySelectorAll<HTMLElement>('.js-bis-drawer-content')
  ];
  let activeContent: HTMLElement | null = null;

  const setActiveContent = (variant: string) => {
    activeContent = null;

    contents.forEach((content) => {
      const isActive = content.dataset.bisVariant === variant;

      content.classList.toggle('hidden', !isActive);
      content.classList.toggle('flex', isActive);

      if (isActive) {
        activeContent = content;
      }
    });

    return activeContent;
  };

  const resetContent = (content: HTMLElement) => {
    const form = content.querySelector<HTMLFormElement>('.js-ls-bis-drawer-subscription');
    const inputEl = form?.querySelector<HTMLInputElement>('.js-ls-input-button__input');
    const submitBtn = form?.querySelector<HTMLButtonElement>('button');
    const subtitleEl = content.querySelector<HTMLElement>('.js-bis-drawer-subtitle');
    const successEl = content.querySelector<HTMLElement>('.js-bis-drawer-success');
    const marketingCheckbox = content.querySelector<HTMLInputElement>('.js-bis-drawer-subscription-checkbox');

    if (submitBtn) {
      submitBtn.textContent = submitBtn.dataset.initialMessage ?? '';
      submitBtn.classList.remove('pointer-events-none');
      submitBtn.removeAttribute('disabled');
    }

    subtitleEl?.classList.remove('hidden');
    successEl?.classList.add('hidden');

    if (inputEl) {
      inputEl.value = '';
    }

    // Restore the checkbox to its initial (checked) state on reopen so the
    // drawer always returns to its default rather than persisting the user's
    // previous selection.
    if (marketingCheckbox) {
      marketingCheckbox.checked = true;
    }
  };

  const openBisDrawer = () => {
    overlay?.classList.remove('hidden', 'pointer-events-none');
    closeButton?.classList.remove('hidden');
  
    requestAnimationFrame(() => { 
      overlay?.classList.remove('opacity-0');
      panel?.classList.remove('translate-x-full');
  
      document.body.classList.add('overflow-hidden');
    });
  };

  const closeBisDrawer = () => {
    overlay?.classList.add('opacity-0');
    panel?.classList.add('translate-x-full');
    cursorClose.hide();
  
    document.body.classList.remove('overflow-hidden');
  
    contents.forEach(resetContent);
  
    setTimeout(() => {
      overlay?.classList.add('hidden', 'pointer-events-none');
      closeButton?.classList.add('hidden');
    }, 400);
  };

  document.addEventListener('bis:open', ((event: CustomEvent) => {
    const triggerProduct = event.detail.product;
    const triggerVariant = event.detail.variant;

    if (!product || !triggerProduct || !triggerVariant) {
      return;
    }

    if (product !== triggerProduct) {
      return;
    }

    if (setActiveContent(triggerVariant)) {
      openBisDrawer();
    }
  }) as EventListener);

  bindAll(section, '.js-bis-drawer-close', 'click', closeBisDrawer);

  overlay?.addEventListener('click', closeBisDrawer);

  // Escape closes this drawer (only when it is the open one).
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel?.classList.contains('translate-x-full')) closeBisDrawer();
  });

  function isValidEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  async function handleSubmit(content: HTMLElement) {
    const form = content.querySelector<HTMLFormElement>('.js-ls-bis-drawer-subscription');
    const inputEl = form?.querySelector<HTMLInputElement>('.js-ls-input-button__input');
    const inputBtnEl = form?.querySelector<HTMLElement>('.js-ls-input-button');
    const submitBtn = form?.querySelector<HTMLButtonElement>('button');
    const marketingCheckbox = content.querySelector<HTMLInputElement>('.js-bis-drawer-subscription-checkbox');
    const subtitleEl = content.querySelector<HTMLElement>('.js-bis-drawer-subtitle');
    const successEl = content.querySelector<HTMLElement>('.js-bis-drawer-success');
    const variant = content.dataset.bisVariant;

    if (!submitBtn) return;
    if (!product) return;
    if (!variant) return;

    const email = inputEl?.value.trim() ?? '';

    if (!email || !isValidEmail(email)) {
      inputBtnEl?.dispatchEvent(
        new CustomEvent('ls-input-button:show-error')
      );
      return;
    }

    submitBtn.setAttribute('disabled', '');
    submitBtn.classList.add('opacity-80');

    try {   
      await BIS.create(
        email,
        variant,
        product
      );
    
      submitBtn.textContent = submitBtn.dataset.successMessage ?? '';
      submitBtn.classList.add('pointer-events-none');

      subtitleEl?.classList.add('hidden');
      successEl?.classList.remove('hidden');
      
      if (marketingCheckbox?.checked && form) {      
        setTimeout(() => {
          form.submit();
        }, 2000)        
      }
    } catch (error) {
      console.error(error);
    } finally {
      submitBtn?.removeAttribute('disabled');
      submitBtn.classList.remove('opacity-80');
    }
  }

  function onSubmitGuard(event: Event) {
    event.preventDefault();
  }

  contents.forEach((content) => {
    const form = content.querySelector<HTMLFormElement>('.js-ls-bis-drawer-subscription');
    const inputEl = form?.querySelector<HTMLInputElement>('.js-ls-input-button__input');
    const submitBtn = form?.querySelector<HTMLButtonElement>('button');

    form?.addEventListener('submit', onSubmitGuard);

    submitBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      handleSubmit(content);
    });

    inputEl?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmit(content);
      }
    });
  });
});

document.addEventListener('click', (event) => {  
  const trigger = (event.target as HTMLElement).closest('.js-bis-trigger') as HTMLElement | null;  

  if (!trigger) return;

  document.dispatchEvent(
    new CustomEvent('bis:open', {
      detail: {
        product: trigger.dataset.product,
        variant: trigger.dataset.variant,
      },
    })
  );
});
